/**
 * CenterContext - Admin이 소유한 center 목록 관리 및 현재 선택된 center 제공
 *
 * users/{uid}/centers subcollection에서 center 목록을 조회하고,
 * 선택된 center를 localStorage에 영속화한다.
 * Center가 하나뿐이면 자동 선택한다.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { collection, getDocs } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { useCurrentUser } from "@/components/AuthProvider";
import { trackEvent, setAnalyticsCenterProperty, EVENT_NAMES } from "@/utils/analytics-tracking";

/** Center 정보 (subcollection document에서 조회) */
export interface CenterInfo {
  centerId: number;
  name: string;
}

export interface CenterContextValue {
  /** 현재 선택된 center (null이면 선택 필요) */
  currentCenter: CenterInfo | null;
  /** Admin이 소유한 center 목록 */
  centers: CenterInfo[];
  /** center 목록 로딩 중 여부 */
  isLoading: boolean;
  /** center를 선택하는 함수 */
  selectCenter: (centerId: number) => void;
  /** center 목록을 Firestore에서 다시 로드하는 함수 */
  refreshCenters: () => Promise<void>;
}

const CenterContext = createContext<CenterContextValue | null>(null);

/** localStorage 키 생성 (사용자별 분리) */
function getStorageKey(uid: string): string {
  return `vlab-selected-center-${uid}`;
}

/**
 * Firestore에서 center 목록을 조회한다.
 * 순수 데이터 fetch 함수로, 상태 업데이트 로직과 분리되어 있다.
 */
async function fetchCentersFromFirestore(uid: string): Promise<CenterInfo[]> {
  const centersRef = collection(getFirebaseFirestore(), "users", uid, "centers");
  const snapshot = await getDocs(centersRef);

  return snapshot.docs.map((doc) => ({
    centerId: Number(doc.id),
    name: (doc.data() as { name: string }).name,
  }));
}

export function CenterProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  const [centers, setCenters] = useState<CenterInfo[]>([]);
  const [currentCenter, setCurrentCenter] = useState<CenterInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // currentCenter를 ref로 유지하여 refreshCenters에서 최신 값 참조
  const currentCenterRef = useRef<CenterInfo | null>(null);
  currentCenterRef.current = currentCenter;

  /**
   * Center 목록을 로드하고 선택 상태를 결정한다.
   *
   * @param isInitialLoad - 초기 로드 여부. true이면 localStorage에서 이전 선택을 복원한다.
   *                        false(refreshCenters)이면 현재 선택된 center가 여전히 존재하는지 확인하고,
   *                        삭제된 경우 첫 번째 center를 자동 선택한다.
   */
  const loadCenters = useCallback(
    async (isInitialLoad: boolean): Promise<void> => {
      setIsLoading(true);
      try {
        const loaded = await fetchCentersFromFirestore(user.uid);

        setCenters(loaded);

        if (isInitialLoad) {
          // Migration: old key -> new key
          const oldKey = `vlab-selected-studio-${user.uid}`;
          const newKey = getStorageKey(user.uid);
          const oldValue = localStorage.getItem(oldKey);
          if (oldValue && !localStorage.getItem(newKey)) {
            localStorage.setItem(newKey, oldValue);
            localStorage.removeItem(oldKey);
          }

          // 초기 로드: localStorage에서 이전 선택 복원 시도
          const storedId = localStorage.getItem(getStorageKey(user.uid));
          if (storedId) {
            const stored = loaded.find((s) => s.centerId === Number(storedId));
            if (stored) {
              setCurrentCenter(stored);
              return;
            }
          }

          // Center가 하나뿐이면 자동 선택
          if (loaded.length === 1) {
            setCurrentCenter(loaded[0]);
            localStorage.setItem(getStorageKey(user.uid), String(loaded[0].centerId));
          }
        } else {
          // refresh: 현재 선택된 center가 여전히 목록에 존재하는지 확인
          const selected = currentCenterRef.current;

          if (selected) {
            const stillExists = loaded.find((s) => s.centerId === selected.centerId);
            if (stillExists) {
              // 이름 등이 변경되었을 수 있으므로 최신 정보로 갱신
              setCurrentCenter(stillExists);
            } else {
              // 선택된 center가 삭제됨 - 첫 번째 center를 자동 선택하거나 선택 해제
              if (loaded.length > 0) {
                setCurrentCenter(loaded[0]);
                localStorage.setItem(getStorageKey(user.uid), String(loaded[0].centerId));
              } else {
                setCurrentCenter(null);
                localStorage.removeItem(getStorageKey(user.uid));
              }
            }
          } else {
            // 선택된 center가 없는 상태에서 새 center가 추가된 경우 자동 선택
            if (loaded.length > 0) {
              setCurrentCenter(loaded[0]);
              localStorage.setItem(getStorageKey(user.uid), String(loaded[0].centerId));
            }
          }
        }
      } catch (error: unknown) {
        console.error("Failed to load centers:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [user.uid],
  );

  // currentCenter가 바뀔 때마다 GA4 user property 갱신
  useEffect(() => {
    if (currentCenter) {
      setAnalyticsCenterProperty(currentCenter.centerId);
    }
  }, [currentCenter]);

  // 초기 Center 목록 로드
  useEffect(() => {
    let cancelled = false;

    loadCenters(true).then(() => {
      // cancelled가 true이면 컴포넌트가 언마운트된 상태이므로 무시
      // (loadCenters 내부의 setState는 React가 자동 무시함)
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [loadCenters]);

  const selectCenter = useCallback(
    (centerId: number) => {
      const center = centers.find((s) => s.centerId === centerId);
      if (center) {
        setCurrentCenter(center);
        localStorage.setItem(getStorageKey(user.uid), String(centerId));
        trackEvent(EVENT_NAMES.CENTER_SELECTED, { center_id: String(centerId) });
      }
    },
    [centers, user.uid],
  );

  /** Center 목록을 Firestore에서 다시 로드한다. */
  const refreshCenters = useCallback(async (): Promise<void> => {
    await loadCenters(false);
  }, [loadCenters]);

  return (
    <CenterContext.Provider
      value={{
        currentCenter,
        centers,
        isLoading,
        selectCenter,
        refreshCenters,
      }}
    >
      {children}
    </CenterContext.Provider>
  );
}

/**
 * CenterContext에 접근하는 훅.
 * CenterProvider 안에서만 사용 가능하다.
 */
export function useCenterContext(): CenterContextValue {
  const context = useContext(CenterContext);
  if (!context) {
    throw new Error("useCenterContext must be used within CenterProvider");
  }
  return context;
}
